// TODO: Instead of relying on old apicaller, export two api callers from the
// new context-based rewrite that handle 406 requests and one that doesn't.
import { OldApiCaller as ApiCaller } from 'actions/ApiCaller';
import { paymentAjaxConfig } from 'actions/BaseAjaxConfig';
import get from 'lodash/get';
import * as constants from 'pages/PaymentsPage/PaymentOptions/constants';
import { addKeysToBanks } from './utils/addKeysToBanks';
import { generateTenureKey } from './utils/tenures';
import apiTransformer, { getDefaultTenure } from './apiTransformer';
import { updateEmiTenureAction } from './emiStateActions';
import * as types from 'typings/Payments/PaymentsTypes';
import { updatePaymentTokenAction } from 'actions/paymentTokenActions';
import { isEmpty } from 'lodash';
import cuid from 'cuid';
import { stringTemplate } from 'components/StringWidget/StringWidget';
import { shouldSkipRender } from 'pages/PaymentsPage/PaymentOptions/EMI/OnSavedCardMessage';
import { RESPONSE_STATUS, RESPONSE_TYPE, PAYMENT_REDIRECTION } from 'pages/PaymentsPage/PaymentOptions/constants';
import { INITIAL_STATE as INITIAL_TOKEN_STATE } from 'reducers/paymentTokenReducer';
import { getHostname } from 'actions/PaymentActions/utils/hostname';
import { UPIPollStatusTypes } from 'pages/PaymentsPage/PaymentOptions/UPI/UPIStatusPoll';
export const GET_PAYMENT_OPTIONS_REQUEST = 'GET_PAYMENT_OPTIONS_REQUEST';
export const GET_PAYMENT_OPTIONS_SUCCESS = 'GET_PAYMENT_OPTIONS_SUCCESS';
export const GET_PAYMENT_OPTIONS_ERROR = 'GET_PAYMENT_OPTIONS_ERROR';
export const RESET_PAYMENT_OPTIONS = 'RESET_PAYMENT_OPTIONS';
const TOKEN_ID_REGEX = /{token_id}/g;
const PAYMENT_INSTRUMENT_REGEX = /{instrument}/g;
const UPI_ID_SUFFIX_REGEX = /{upi_id_suffix}/g;
// Caution: This assumes the instrument part of the query paramter always shows
// up at the end.
const PAYMENT_INSTRUMENT_QUERY_PARAM_REGEX = /\&instrument={instrument}/g;
const TOKEN_ID_QUERY_PARAM_REGEX = /\?token={token_id}/g;
const DEFAULT_TOKEN_VERSION = types.ITokenVersion.V3;
export const parseTokenParams = (token) => {
    if (!token) {
        return { tokenVersion: DEFAULT_TOKEN_VERSION, dcId: types.IDcId.UNKNOWN_DC };
    }
    // the payment token is of the format "alphaNumericToken_tokenVersion_dcId"
    const splitToken = token.split('_');
    const tokenVersion = splitToken[1] || DEFAULT_TOKEN_VERSION;
    const dcId = splitToken[2] || types.IDcId.UNKNOWN_DC;
    return { tokenVersion, dcId };
};
const getCheckoutExperiments = (state) => {
    const experiments = get(state, ['userState', 'abResponse', 'abExperiments']) || [];
    return experiments
        .filter(exp => exp && exp.appId === 'Checkout')
        .reduce((p, exp) => Object.assign(p, exp.metadata), {});
};
// This function handles:
//
// 1. parsing of token by picking it up from reducer
// 2. Figuring out the correct pathname to call, based on the endpoint constants passed
// 3. When payments is running in degraded mode, we need to switch the payment API
// host.
export const getPaymentAjaxConfig = (getState, options) => {
    // re-copying this again because paymentsajaxconfig is used in multiple
    // other files
    const ajaxConfig = Object.assign({}, paymentAjaxConfig, {
        acceptErrors: true,
        acceptJson: false
    });
    if (typeof getState === 'function') {
        const state = getState();
        const paymentTokenDetails = get(state, 'paymentToken', INITIAL_TOKEN_STATE);
        const { tokenVersion, token, dcId } = paymentTokenDetails;
        Object.assign(ajaxConfig, {
            hostname: __PAYMENT_API_HOST__ || getHostname(dcId, options.pathname)
        });
        let pathname = constants.API_ENDPOINTS[tokenVersion][options.pathname];
        if (pathname) {
            pathname = token
                ? pathname.replace(TOKEN_ID_REGEX, token)
                : pathname.replace(TOKEN_ID_QUERY_PARAM_REGEX, '').replace(TOKEN_ID_REGEX, '');
            const paymentInstrumentReplaceString = options.payment_instrument ? options.payment_instrument : '';
            pathname = paymentInstrumentReplaceString
                ? pathname.replace(PAYMENT_INSTRUMENT_REGEX, paymentInstrumentReplaceString)
                : pathname.replace(PAYMENT_INSTRUMENT_QUERY_PARAM_REGEX, '');
            const { upiIdSuffix } = options;
            if (upiIdSuffix) {
                pathname = pathname.replace(UPI_ID_SUFFIX_REGEX, upiIdSuffix);
            }
        }
        const checkoutExperiments = getCheckoutExperiments(state);
        const paymentsABs = constants.backendPoweredExperiments.reduce((aggregate, key) => {
            if (checkoutExperiments[key])
                aggregate[key] = checkoutExperiments[key];
            return aggregate;
        }, {});
        // gpay ab header. should be removed when backend starts powering without this header
        const enableUPIPayment = get(state, ['config', 'data', 'globalConfig', 'enableUPIPayment']) || false;
        if (enableUPIPayment) {
            const gpayIntegrationABHeaderValue = 2;
            paymentsABs['gpay_integration'] = gpayIntegrationABHeaderValue;
        }
        if (!paymentsABs['wallet_experiment']) {
            paymentsABs['wallet_experiment'] = 1;
        }
        if (!paymentsABs['NU_COD_DEFAULT']) {
            paymentsABs['NU_COD_DEFAULT'] = 1;
        }
        if (!paymentsABs['vpa_payments_page']) {
            paymentsABs['vpa_payments_page'] = 1;
        }
        const headers = Object.keys(paymentsABs).length
            ? Object.assign({}, ajaxConfig.headers, {
                'x-ab-experiments': JSON.stringify(paymentsABs),
                'x-client-trace-id': cuid()
            })
            : Object.assign({}, ajaxConfig.headers, { 'x-client-trace-id': cuid() });
        Object.assign(ajaxConfig, { pathname, headers });
    }
    return ajaxConfig;
};
const normalizeSuccess = (response) => {
    if (response.ok) {
        // Try parsing json. We don't have a check to see if the json prop
        // exists because if this was a 200 response, the assumption is that
        // the object _will_ be there. Any different is a much serious bug at
        // contract level, and our careful handling won't matter much.
        return response
            .json()
            .then(json => Promise.resolve({ response: json, status: response.status }))
            .catch(_ => {
            // Catching, because if the body is null, JSON decoding is
            // bound to fail. So we ignore the error object, and just
            // send the status code
            return Promise.reject({ response: null, status: constants.NETWORK_ERROR.JSON_PARSING_ERROR });
        });
    }
    // A successful, but non 200 response. Like a 300. For all intents and
    // purposes, we can't handle this error properly. Ideally, this should
    // _never_ happen. So may be notify backend teams?
    return Promise.reject({ response: null, status: constants.NETWORK_ERROR.NON_200_SUCCESS_RESPONSE });
};
// Using this function to consume the error response everytime if present.
// This is intended for use only in payAction and paywithDetailsAction as those APIs have a valid
// usecase for consuming the error response, for now.
// TODO:1 - This logic should be moved into `normalizeError`
// TODO:2 - Fix promise rejection logic in `normalizeError` when status code is not present
const normalizeErrorWithoutStatusCodeCheck = (response) => {
    if (typeof response.json === 'function') {
        return response.json().then(json => Promise.reject({ response: json, status: response.status || constants.NETWORK_ERROR.NO_STATUS_CODE }), _ => Promise.reject({ response: null, status: constants.NETWORK_ERROR.JSON_PARSING_ERROR }));
    }
    return Promise.reject({ response: null, status: constants.NETWORK_ERROR.NO_STATUS_CODE });
};
const normalizeError = (response) => {
    if (response.status) {
        // If a statuscode is present, we can assume (for now), that the
        // response body is not present, because as of this point, we don't
        // get bodies for most of the non-200 responses from fkpay or mapi.
        return Promise.reject({ response: null, status: response.status });
    }
    if (typeof response.json === 'function') {
        return response
            .json()
            .then(json => Promise.reject({ response: json, status: constants.NETWORK_ERROR.NO_STATUS_CODE }))
            .catch(_ => Promise.reject({ response: null, status: constants.NETWORK_ERROR.JSON_PARSING_ERROR }));
    }
    // we got some response that's not in JSON format. Outright reject this
    // with a blank status code
    return Promise.reject({ response: null, status: constants.NETWORK_ERROR.NO_STATUS_CODE });
};
function checkForResponseStatus(successData) {
    const actionType = get(successData, 'response.primary_action.action_type');
    const responseType = get(successData, 'response.response_type');
    const responseStatus = get(successData, 'response.response_status');
    // Skip check of responseStatus if the response is a merchant failure
    // redirection + external redirection, and default to showing the error
    // modal irrespective of the "response_status" field. This is usually the
    // case in cart/checkout diff and checkout session expiry.
    const skipCheck = responseType === constants.RESPONSE_TYPE.MERCHANT_FAILURE_REDIRECTION &&
        (actionType === constants.PAYMENT_REDIRECTION.EXTERNAL ||
            actionType === constants.PAYMENT_REDIRECTION.MERCHANT_REDIRECTION);
    if (skipCheck || responseStatus === constants.RESPONSE_STATUS.FAILED) {
        return Promise.reject({ response: successData.response, status: constants.NETWORK_ERROR.KNOWN_PAYMENT_ERROR });
    }
    return Promise.resolve(successData);
}
export function getPaymentOptionsAction() {
    return { type: GET_PAYMENT_OPTIONS_REQUEST };
}
// Keys that are not used for price summary, and thus filtered out:
//
// price
// base_price (moved one level up)
// offer_saving
const V3DisplayTextMap = {
    pickup_charges: stringTemplate('cartSummaryPickupCharges'),
    giftwrap_charges: stringTemplate('cartSummaryGiftWrapCharges'),
    delivery_charges: stringTemplate('cartSummaryDeliveryCharges'),
    octroi_charges: stringTemplate('cartSummaryPickupCharges')
};
// TODO: This should return only PaymentOptionsV3 format
export function updatePriceSummaryStructure(data) {
    // If price summary is not present in the response (happens when wallet
    // select/unselect returned a failure message) avoid changing the
    // object. This will ensure we are not modifying the price widget
    // information
    const priceSummary = get(data.response, 'price_summary');
    // TODO: Add a check for empty priceSummary
    if (!priceSummary) {
        return data.response;
    }
    let tokenVersion = DEFAULT_TOKEN_VERSION;
    if (typeof data.getState === 'function') {
        const token = get(data.getState(), 'paymentToken.token');
        tokenVersion = parseTokenParams(token).tokenVersion;
    }
    if (tokenVersion === types.ITokenVersion.V3) {
        return data.response;
    }
    let finalData = Object.assign({}, data.response);
    // preprocess the data, and update the structure to match other api
    // versions;
    // using any as the return type here because the breakup object doesn't yet have swagger spec
    // TODO: Get the swagger spec sorted out, and use that type here
    const breakup = get(finalData, 'price_summary.breakup', {});
    if (isEmpty(breakup)) {
        return finalData;
    }
    const keys = Object.keys(breakup);
    const validKeys = Object.keys(V3DisplayTextMap);
    const newBreakup = keys
        .filter(key => validKeys.indexOf(key) > -1)
        .reduce((aggregate, key) => {
        aggregate.push({
            key,
            display_text: V3DisplayTextMap[key],
            value: breakup[key],
            type: 'DEFAULT'
        });
        return aggregate;
    }, []);
    // TODO: add null checks?
    const newPriceSummary = Object.assign({}, finalData.price_summary, {
        breakup: newBreakup,
        base_price: breakup.base_price
    });
    finalData.price_summary = newPriceSummary;
    return finalData;
}
export const getCardId = option => get(option, 'card_information.card_id', '');
// HACK: This boolean flag lets the component know if it has to avoid
// rendering entirely. Starting with EMI on saved cards changes Bajaj
// FinServ cards start coming up, and they don't have a FULL_PAYMENT
// option. So they should _always_ be shown with the new experience.
// However, since we have a AB running to enable/disable the new EMI on
// saved card flow, until it's concluded we won't show the Bajaj
// FinServ saved card. So removing bajaj cards from options list based on AB
//
//
// NOTE: This also handles adding a flag to enable/disable the emi flow, so
// when removing bajaj hack, ensure this is not removed
function removeBajajFinservCardsBasedOnAB(data) {
    const options = get(data, 'response.options', []);
    // If the options list doesn't have bajaj finserv cards, bail out early
    if (!options.length) {
        return data.response;
    }
    const newOptions = options
        .map(option => {
        const isEMIOnSavedCardsFlow = Boolean(option.emi_display_text);
        if (shouldSkipRender({ option, isEMIOnSavedCardsFlow })) {
            return null;
        }
        // Add a flag for setting the emi on saved cards. This is much simpler
        // than fiddling with instrument check data. It's assumed that if the
        // flow is to be enabled, the display text will always be there.
        return Object.assign({}, option, { emi_on_saved_cards: isEMIOnSavedCardsFlow });
    })
        .filter(option => option);
    const modifiedResponse = Object.assign({}, data.response, { options: newOptions });
    return modifiedResponse;
}
export function doNecessaryTransformations(data) {
    return removeBajajFinservCardsBasedOnAB({ response: updatePriceSummaryStructure(data), getState: data.getState });
}
export function getPaymentOptionsSuccessAction(data) {
    return { type: GET_PAYMENT_OPTIONS_SUCCESS, data };
}
export function getPaymentOptionsErrorAction({ response, status }) {
    return { type: GET_PAYMENT_OPTIONS_ERROR, error: response, status };
}
export function resetPaymentOptionsActions() {
    return { type: RESET_PAYMENT_OPTIONS };
}
export const VOUCHER_ADD_OR_SELECT_REQUEST = 'VOUCHER_ADD_OR_SELECT_REQUEST';
export const VOUCHER_ADD_OR_SELECT_SUCCESS = 'VOUCHER_ADD_OR_SELECT_SUCCESS';
export const VOUCHER_ADD_OR_SELECT_ERROR = 'VOUCHER_ADD_OR_SELECT_ERROR';
export function getVoucherAddSelectAction() {
    return { type: VOUCHER_ADD_OR_SELECT_REQUEST };
}
export function getVoucherAddSelectSuccessAction(data) {
    return { type: VOUCHER_ADD_OR_SELECT_SUCCESS, data };
}
export function getVoucherAddSelectErrorAction({ response, status }) {
    return { type: VOUCHER_ADD_OR_SELECT_ERROR, error: response, status };
}
export function addEGVAction(number, pin, token) {
    return (dispatch, getState) => {
        dispatch(getVoucherAddSelectAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.ADD_EGV })), { action: VOUCHER_ADD_OR_SELECT_REQUEST }), {
            egv_number: number,
            egv_pin: pin,
            token
        })
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => {
            const updatedResponse = doNecessaryTransformations({ response: successData.response, getState });
            return dispatch(getVoucherAddSelectSuccessAction(updatedResponse));
        }, (errorData) => dispatch(getVoucherAddSelectErrorAction(errorData)));
    };
}
export function fetchPaymentOptionsAction(params) {
    return (dispatch, getState) => {
        dispatch(updatePaymentTokenAction(params.token));
        const paymentOptionsConfig = Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.PAYMENT_OPTIONS }), {
            action: GET_PAYMENT_OPTIONS_REQUEST
        });
        dispatch(getPaymentOptionsAction());
        return ApiCaller.post(paymentOptionsConfig, params)
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => {
            const updatedResponse = doNecessaryTransformations({ response: successData.response, getState });
            return dispatch(getPaymentOptionsSuccessAction(updatedResponse));
        }, (errorData) => dispatch(getPaymentOptionsErrorAction(errorData)));
    };
}
export const PAYMENT_OPTION_SUCCESS_AND_MODIFY_SELECTED_OPTION = 'PAYMENT_OPTION_SUCCESS_AND_MODIFY_SELECTED_OPTION';
export function modifySelectedPaymentOption(data, paymentOption) {
    return {
        type: PAYMENT_OPTION_SUCCESS_AND_MODIFY_SELECTED_OPTION,
        paymentOption,
        data
    };
}
// This action first removes all the payment options (except payment token),
// then starts fetching them. One difference between
// `fetchPaymentOptionsAction` and this is that when successful, it will also
// call the `updateSelectedPaymentOptionAction` function to ensure the
// user-selected option is highlighted correctly.
export function reFetchPaymentOptionsAction(paymentOption) {
    return (dispatch, getState) => {
        dispatch(resetPaymentOptionsActions());
        // TODO: handle empty token case gracefully
        const token = get(getState(), 'paymentToken.token');
        const paymentOptionsConfig = Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.PAYMENT_OPTIONS }), {
            action: GET_PAYMENT_OPTIONS_REQUEST
        });
        dispatch(getPaymentOptionsAction());
        return ApiCaller.post(paymentOptionsConfig, { token })
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => {
            const updatedResponse = doNecessaryTransformations({ response: successData.response, getState });
            return dispatch(modifySelectedPaymentOption(updatedResponse, paymentOption));
        }, (errorData) => dispatch(getPaymentOptionsErrorAction(errorData)));
    };
}
export const INSTRUMENT_CHECK_REQUEST = 'INSTRUMENT_CHECK_REQUEST';
export const INSTRUMENT_CHECK_SUCCESS = 'INSTRUMENT_CHECK_SUCCESS';
export const INSTRUMENT_CHECK_ERROR = 'INSTRUMENT_CHECK_ERROR';
export const INSTRUMENT_CHECK_RESET = 'INSTRUMENT_CHECK_RESET';
export function instrumentCheckResetAction(cardId) {
    return (dispatch, getState) => {
        const instrumentCheck = get(getState(), ['payments', 'instrumentCheck', cardId], null);
        if (!instrumentCheck || isEmpty(instrumentCheck))
            dispatch({ type: INSTRUMENT_CHECK_RESET, cardId });
    };
}
export function instrumentCheckRequestAction(cardId) {
    return { type: INSTRUMENT_CHECK_REQUEST, cardId };
}
// TODO: cardId here is not specific enough. It is a mix of payment type or
// the actual card_id from card_information field for saved cards
export function instrumentCheckSuccessAction(data, cardId, requestData) {
    return { type: INSTRUMENT_CHECK_SUCCESS, data, cardId, requestData };
}
export function instrumentCheckErrorAction(error) {
    return { type: INSTRUMENT_CHECK_ERROR, error };
}
export function instrumentCheckAction(instrumentData, cardKey) {
    const upiCode = (instrumentData.upi_details && instrumentData.upi_details.upi_code) || '';
    const id = cardKey || instrumentData.card_id || upiCode || instrumentData.payment_instrument;
    return (dispatch, getState) => {
        const settings = Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.INSTRUMENT_CHECK }), {
            action: INSTRUMENT_CHECK_REQUEST,
            /// resetting these values because right now we don't handle
            //instrument check failures
            acceptErrors: false,
            acceptJson: true
        });
        const body = Object.assign({}, instrumentData);
        const state = getState();
        dispatch(instrumentCheckRequestAction(id));
        // FIXME: Ideally, instrument check should not do this, may be.
        if (cardKey) {
            // TODO: Move this away from any type and use the initial state
            // from emistate reducer
            const emiState = get(state, 'payments.emiState') || {};
            const tenure = Number(emiState.selectedTenure) || 0;
            // FIXME: Bank code is not required for EMI. Recheck this.
            const bankCode = emiState.selectedBank;
            const emiType = emiState.selectedBankEmiType;
            Object.assign(body, {
                bank_code: bankCode,
                tenure,
                emi_type: emiType
            });
        }
        return ApiCaller.post(settings, body).then((resp) => {
            return dispatch(instrumentCheckSuccessAction(resp, id, body));
        }, err => dispatch(instrumentCheckErrorAction(err)));
    };
}
export const PAY_WITH_DETAILS_REQUEST = 'PAY_WITH_DETAILS_REQUEST';
export const PAY_WITH_DETAILS_SUCCESS = 'PAY_WITH_DETAILS_SUCCESS';
export const PAY_WITH_DETAILS_ERROR = 'PAY_WITH_DETAILS_ERROR';
export function payWithDetailsRequestAction() {
    return { type: PAY_WITH_DETAILS_REQUEST };
}
export function payWithDetailsSuccessAction(data) {
    return { type: PAY_WITH_DETAILS_SUCCESS, data };
}
export function payWithDetailsErrorAction({ response, status }) {
    return { type: PAY_WITH_DETAILS_ERROR, error: response, status };
}
export function payWithDetailsAction(paymentInfo, cardKey) {
    return (dispatch, getState) => {
        const settings = Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.PAY_WITH_DETAILS }), {
            action: PAY_WITH_DETAILS_REQUEST
        });
        // TODO: For pay with details, bank code is not being sent by fk-cp-pay.
        // Reverify this once again.
        if (cardKey) {
            // If cardKey is present, this is EMI flow.
            Object.assign(paymentInfo, {
                payment_instrument: constants.PAYMENT_INSTRUMENT.EMI
            });
            const emiState = get(getState(), ['payments', 'emiState']);
            const tenure = get(emiState, ['selectedTenure']);
            const emiType = get(emiState, ['selectedBankEmiType']);
            Object.assign(paymentInfo, { tenure, emi_type: emiType });
        }
        dispatch(payWithDetailsRequestAction());
        return ApiCaller.post(settings, paymentInfo)
            .then(normalizeSuccess, normalizeErrorWithoutStatusCodeCheck)
            .then(checkForResponseStatus)
            .then((successData) => dispatch(payWithDetailsSuccessAction(successData.response)), (errorData) => dispatch(payWithDetailsErrorAction(errorData)));
    };
}
export const NET_OPTIONS_REQUEST = 'NET_OPTIONS_REQUEST';
export const NET_OPTIONS_SUCCESS = 'NET_OPTIONS_SUCCESS';
export const NET_OPTIONS_ERROR = 'NET_OPTIONS_ERROR';
export function netOptionsRequestAction() {
    return { type: NET_OPTIONS_REQUEST };
}
export function netOptionsSuccessAction(data) {
    return { type: NET_OPTIONS_SUCCESS, data };
}
export function netOptionsErrorAction(error) {
    return { type: NET_OPTIONS_ERROR, error };
}
export function fetchNetOptionsAction(token) {
    return (dispatch, getState) => {
        dispatch(netOptionsRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.NET_BANK_LIST })), { action: NET_OPTIONS_REQUEST, acceptErrors: false, acceptJson: true }), { token }).then(data => dispatch(netOptionsSuccessAction(data)), err => {
            return dispatch(netOptionsErrorAction(err));
        });
    };
}
export const UPI_OPTIONS_REQUEST = 'UPI_OPTIONS_REQUEST';
export const UPI_OPTIONS_SUCCESS = 'UPI_OPTIONS_SUCCESS';
export const UPI_OPTIONS_ERROR = 'UPI_OPTIONS_ERROR';
export function upiOptionsRequestAction() {
    return { type: UPI_OPTIONS_REQUEST };
}
export function upiOptionsSuccessAction(data) {
    return { type: UPI_OPTIONS_SUCCESS, data };
}
export function upiOptionsErrorAction(error) {
    return { type: UPI_OPTIONS_ERROR, error };
}
export function fetchUPIOptionsAction(token) {
    return (dispatch, getState) => {
        dispatch(upiOptionsRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.UPI_OPTIONS_LIST })), { action: UPI_OPTIONS_REQUEST, acceptErrors: false, acceptJson: true }), { token }).then(data => dispatch(upiOptionsSuccessAction(data)), err => dispatch(upiOptionsErrorAction(err)));
    };
}
export const UPI_POLL_INFO_REQUEST = 'UPI_POLL_INFO_REQUEST';
export const UPI_POLL_INFO_SUCCESS = 'UPI_POLL_INFO_SUCCESS';
export const UPI_POLL_INFO_ERROR = 'UPI_POLL_INFO_ERROR';
export function upiPollInfoRequestAction() {
    return { type: UPI_POLL_INFO_REQUEST };
}
export function upiPollInfoSuccessAction(data) {
    return { type: UPI_POLL_INFO_SUCCESS, data };
}
export function upiPollInfoErrorAction(error) {
    return { type: UPI_POLL_INFO_ERROR, error };
}
export function fetchUPIPollInfoAction(upiIdSuffix) {
    return (dispatch, getState) => {
        dispatch(upiPollInfoRequestAction());
        return ApiCaller.get(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, {
            pathname: constants.API_ENDPOINT_NAME.UPI_POLL_INFO,
            upiIdSuffix
        })), { action: UPI_POLL_INFO_REQUEST, acceptErrors: false, acceptJson: true })).then(data => dispatch(upiPollInfoSuccessAction({ [upiIdSuffix]: data.info })), err => dispatch(upiPollInfoErrorAction(err)));
    };
}
export const UPI_STATUS_POLL_REQUEST = 'UPI_STATUS_POLL_REQUEST';
export const UPI_STATUS_POLL_SUCCESS = 'UPI_STATUS_POLL_SUCCESS';
export const UPI_STATUS_POLL_ERROR = 'UPI_STATUS_POLL_ERROR';
export const UPI_STATUS_POLL_TIMEOUT_ERROR = 'UPI_STATUS_POLL_TIMEOUT_ERROR';
export function upiStatusPollRequestAction() {
    return { type: UPI_STATUS_POLL_REQUEST };
}
export function upiStatusPollSuccessAction(data) {
    return { type: UPI_STATUS_POLL_SUCCESS, data };
}
export function upiStatusPollErrorAction(error, status) {
    return { type: UPI_STATUS_POLL_ERROR, error, status };
}
export function upiStatusPollTimeoutErrorAction(status) {
    return { type: UPI_STATUS_POLL_TIMEOUT_ERROR, status };
}
export function pollUPIStatusAction(token, transactionId) {
    return (dispatch, getState) => {
        dispatch(upiStatusPollRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.UPI_STATUS_POLL })), { action: UPI_STATUS_POLL_REQUEST, acceptErrors: false, acceptJson: true }), { token, transactionId }).then(data => {
            if (data.response_status === UPIPollStatusTypes.FAILED) {
                dispatch(upiStatusPollErrorAction(data, constants.UPI_ERROR.STATUS_POLL_ERROR));
            }
            else {
                dispatch(upiStatusPollSuccessAction(data));
            }
        }, () => {
            const error = {
                response_status: UPIPollStatusTypes.FAILED,
                response_message: null // to ensure default error messaging
            };
            dispatch(upiStatusPollErrorAction(error, constants.UPI_ERROR.STATUS_POLL_ERROR));
        });
    };
}
export const EMI_BANKS_REQUEST = 'EMI_BANKS_REQUEST';
export const EMI_BANKS_SUCCESS = 'EMI_BANKS_SUCCESS';
export const EMI_BANKS_ERROR = 'EMI_BANKS_ERROR';
export function emiBanksRequestAction() {
    return { type: EMI_BANKS_REQUEST };
}
export function emiBanksSuccessAction(data) {
    return { type: EMI_BANKS_SUCCESS, data };
}
export function emiBanksErrorAction(error) {
    return { type: EMI_BANKS_ERROR, error };
}
export function fetchEmiBanksAction(token) {
    return (dispatch, getState) => {
        dispatch(emiBanksRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.EMI_OPTIONS_LIST })), { action: EMI_BANKS_REQUEST, acceptErrors: false, acceptJson: true }), { token }).then(data => {
            const keyedData = addKeysToBanks(data);
            return dispatch(emiBanksSuccessAction(keyedData));
        }, err => {
            return dispatch(emiBanksErrorAction(err));
        });
    };
}
export const EMI_TENURES_REQUEST = 'EMI_TENURES_REQUEST';
export const EMI_TENURES_SUCCESS = 'EMI_TENURES_SUCCESS';
export const EMI_TENURES_ERROR = 'EMI_TENURES_ERROR';
export function emiTenuresRequestAction(key) {
    return { type: EMI_TENURES_REQUEST, key };
}
export function emiTenuresSuccessAction(data, key, emiType, emiState) {
    const transformedData = apiTransformer(data, emiType);
    return {
        type: EMI_TENURES_SUCCESS,
        data: transformedData,
        key,
        emiState
    };
}
export function emiTenuresErrorAction(error, key) {
    return { type: EMI_TENURES_ERROR, error, key };
}
export function fetchEmiTenuresAction(requestData) {
    return (dispatch, getState) => {
        // TODO: Once the tenures API is completely migrated to v2 and new
        // design, remove the section_code argument;
        const sectionCode = requestData.section_code;
        // FIXME: The key here is wrong. For both EMI with interest and
        // debit card emi, the key would be the same. We should be using the `key`
        // parameter from the emi/bank response.
        const key = generateTenureKey(requestData);
        dispatch(emiTenuresRequestAction(key));
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.EMI_TENURES })), { action: EMI_TENURES_REQUEST, acceptErrors: false, acceptJson: true }), requestData).then(data => {
            const emiState = getState()['payments']['emiState'];
            // TODO: Need to verify if _not_ returning this causes any issues.
            dispatch(emiTenuresSuccessAction(data, key, sectionCode, emiState));
            const tenure = getDefaultTenure(data, emiState);
            if (tenure) {
                return dispatch(updateEmiTenureAction(tenure));
            }
        }, err => {
            return dispatch(emiTenuresErrorAction(err, key));
        });
    };
}
export const EMI_2FA_REQUEST = 'EMI_2FA_REQUEST';
export const EMI_2FA_SUCCESS = 'EMI_2FA_SUCCESS';
export const EMI_2FA_ERROR = 'EMI_2FA_ERROR';
export function emi2FARequestAction() {
    return { type: EMI_2FA_REQUEST };
}
export function emi2FASuccessAction(data) {
    return { type: EMI_2FA_SUCCESS, data };
}
export function emi2FAErrorAction(error) {
    return { type: EMI_2FA_ERROR, error };
}
export function fetchEmi2FADetailsAction(requestData) {
    return (dispatch, getState) => {
        dispatch(emi2FARequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.EMI_2FA_MODES })), { action: EMI_2FA_REQUEST, acceptErrors: false, acceptJson: true }), requestData).then(data => {
            return dispatch(emi2FASuccessAction(data));
        }, err => {
            return dispatch(emi2FAErrorAction(err));
        });
    };
}
export var EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE;
(function (EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE) {
    EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE["REQUEST"] = "EMI_ON_SAVED_CARDS_TENURES_REQUEST";
    EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE["SUCCESS"] = "EMI_ON_SAVED_CARDS_TENURES_SUCCESS";
    EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE["ERROR"] = "EMI_ON_SAVED_CARDS_TENURES_ERROR";
    EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE["RESET"] = "EMI_ON_SAVED_CARDS_TENURES_RESET";
})(EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE || (EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE = {}));
export const emiOnSavedCardTenuresActions = {
    ERROR(error, key) {
        return {
            type: EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE.ERROR,
            error,
            key
        };
    },
    SUCCESS(data, key) {
        return {
            type: EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE.SUCCESS,
            data,
            key
        };
    },
    REQUEST(key) {
        return {
            type: EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE.REQUEST,
            key
        };
    },
    RESET(key) {
        return {
            type: EMI_ON_SAVED_CARDS_TENURES_ACTION_STATE.RESET,
            key
        };
    }
};
export function fetchEMIOnSavedCardTenuresAction(requestData) {
    return (dispatch, getState) => {
        const key = requestData.card_id;
        dispatch(emiOnSavedCardTenuresActions.REQUEST(key));
        const token = get(getState(), 'paymentToken.token');
        const modifiedRequestData = Object.assign({}, requestData, { token });
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.EMI_TENURES })), { action: EMI_TENURES_REQUEST, acceptErrors: false, acceptJson: true }), modifiedRequestData).then(data => {
            return dispatch(emiOnSavedCardTenuresActions.SUCCESS(data, key));
        }, err => {
            return dispatch(emiOnSavedCardTenuresActions.ERROR(err, key));
        });
    };
}
export const EMI_ITEM_LEVEL_BREAKUP_REQUEST = 'EMI_ITEM_LEVEL_BREAKUP_REQUEST';
export const EMI_ITEM_LEVEL_BREAKUP_RESET = 'EMI_ITEM_LEVEL_BREAKUP_RESET';
export const EMI_ITEM_LEVEL_BREAKUP_SUCCESS = 'EMI_ITEM_LEVEL_BREAKUP_SUCCESS';
export const EMI_ITEM_LEVEL_BREAKUP_ERROR = 'EMI_ITEM_LEVEL_BREAKUP_ERROR';
const emiItemLevelBreakupRequestAction = () => {
    return {
        type: EMI_ITEM_LEVEL_BREAKUP_REQUEST
    };
};
export const emiResetItemLevelBreakupErrorAction = () => {
    return {
        type: EMI_ITEM_LEVEL_BREAKUP_RESET
    };
};
const emiItemLevelBreakupSuccessAction = breakup => {
    return {
        type: EMI_ITEM_LEVEL_BREAKUP_SUCCESS,
        breakup
    };
};
const emiItemLevelBreakupErrorAction = ({ response, status }) => {
    return {
        type: EMI_ITEM_LEVEL_BREAKUP_ERROR,
        error: response,
        status
    };
};
const NullItemLevelBreakupOptions = {
    paymentInstrument: '',
    cardNumber: '',
    cardId: '',
    bankCode: '',
    tenure: 0,
    emiType: ''
};
export const fetchEmiItemLevelBreakupAction = (opts) => {
    return (dispatch, getState) => {
        dispatch(emiItemLevelBreakupRequestAction());
        const state = getState();
        const token = get(state, 'paymentToken.token');
        const settings = Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.ITEM_LEVEL_BREAK_UP }), {
            action: EMI_ITEM_LEVEL_BREAKUP_REQUEST
        });
        const options = !isEmpty(opts) ? opts : NullItemLevelBreakupOptions;
        const { paymentInstrument, cardNumber, cardId, bankCode, tenure, emiType } = options;
        // from emi cards page later:
        //
        // {
        //   bank_code: get(getState(), 'emiState.selectedBank', ''),
        //   tenure: '',
        //   emi_type: get(getState(), 'emiState.selectedSection', ''),
        //   payment_instrument: constants.PAYMENT_INSTRUMENT.EMI,
        //   card_number: '',
        //   card_id: ''
        // };
        const emiParams = {
            bank_code: bankCode || get(state, 'payments.emiState.selectedBank', ''),
            tenure: tenure || get(state, 'payments.emiState.selectedTenure', ''),
            emi_type: emiType || get(state, 'payments.emiState.selectedBankEmiType', ''),
            token
        };
        if (paymentInstrument)
            Object.assign(emiParams, { payment_instrument: paymentInstrument });
        if (cardNumber)
            Object.assign(emiParams, { card_number: cardNumber });
        if (cardId)
            Object.assign(emiParams, { card_id: cardId });
        return ApiCaller.post(settings, emiParams)
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => dispatch(emiItemLevelBreakupSuccessAction(successData.response)), (errorData) => dispatch(emiItemLevelBreakupErrorAction(errorData)));
    };
};
export var PAY_ACTION_STATE;
(function (PAY_ACTION_STATE) {
    PAY_ACTION_STATE["REQUEST"] = "PAY_REQUEST";
    PAY_ACTION_STATE["SUCCESS"] = "PAY_SUCCESS";
    PAY_ACTION_STATE["ERROR"] = "PAY_ERROR";
    PAY_ACTION_STATE["RESET"] = "RESET_PAY";
})(PAY_ACTION_STATE || (PAY_ACTION_STATE = {}));
// TODO: Remove the below exports everywhere
export const PAY_REQUEST = PAY_ACTION_STATE.REQUEST;
export const PAY_SUCCESS = PAY_ACTION_STATE.SUCCESS;
export const PAY_ERROR = PAY_ACTION_STATE.ERROR;
export const RESET_PAY = PAY_ACTION_STATE.RESET;
export function payRequestAction() {
    return { type: PAY_REQUEST };
}
export function paySuccessAction(data) {
    return { type: PAY_SUCCESS, data };
}
export function payErrorAction({ response, status }) {
    return { type: PAY_ERROR, error: response, status };
}
export function payAction(payData, cardKey) {
    return (dispatch, getState) => {
        dispatch(payRequestAction());
        if (cardKey) {
            Object.assign(payData, { payment_instrument: constants.PAYMENT_INSTRUMENT.EMI });
        }
        const token = get(getState(), 'paymentToken.token');
        Object.assign(payData, { token });
        const settings = Object.assign({}, getPaymentAjaxConfig(getState, {
            pathname: constants.API_ENDPOINT_NAME.SUBMIT_PAY,
            payment_instrument: payData['payment_instrument']
        }), { action: PAY_REQUEST });
        return ApiCaller.post(settings, payData)
            .then(normalizeSuccess, normalizeErrorWithoutStatusCodeCheck)
            .then(checkForResponseStatus)
            .then(r => Promise.resolve(r), (errorData) => {
            const responseStatus = get(errorData, 'response.response_status');
            if (responseStatus === constants.RESPONSE_STATUS.FAILED) {
                // Figure out if the error is because the user entered a wrong captcha
                // input. If so, avoid calling the pay error action because that would
                // show up the common error message. In this case, there would be an
                // error message that's returned from the server, and we need to show
                // that instead.
                //
                // TODO: what about other cases? The default action when failure
                // response is returned is to reject the promise, which will manifest
                // into an error retry component in the UI. Is this expected for cases
                // other than wrong CAPTCHA entry? If so, how should they be handled?
                const errorCodes = get(errorData.response, 'messages', [])
                    .map(message => get(message, 'status_code', null))
                    .filter(i => i);
                if (errorCodes.indexOf('INCORRECT_CAPTCHA_OR_TIME_LIMIT_EXCEEDED') > -1) {
                    // We don't care about the error case here. We need to show it in the
                    // UI, so we set the state to success, which will remove the loader.
                    return Promise.resolve(errorData);
                }
            }
            return Promise.reject(errorData);
        })
            .then((successData) => dispatch(paySuccessAction(successData.response)), (errorData) => dispatch(payErrorAction(errorData)));
    };
}
export const EMI_TERMS_REQUEST = 'EMI_TERMS_REQUEST';
export const EMI_TERMS_SUCCESS = 'EMI_TERMS_SUCCESS';
export const EMI_TERMS_ERROR = 'EMI_TERMS_ERROR';
export function emiTermsRequestAction() {
    return { type: EMI_TERMS_REQUEST };
}
export function emiTermsSuccessAction(data) {
    return { type: EMI_TERMS_SUCCESS, data };
}
export function emiTermsErrorAction(error) {
    return { type: EMI_TERMS_ERROR, error };
}
export function emiTermsAction() {
    return (dispatch, getState) => {
        dispatch(emiTermsRequestAction());
        return ApiCaller.get(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.EMI_FAQ_TERMS })), { action: EMI_TERMS_REQUEST, acceptErrors: false, acceptJson: true })).then(data => {
            return dispatch(emiTermsSuccessAction(data));
        }, err => {
            return dispatch(emiTermsErrorAction(err));
        });
    };
}
export const GET_CAPTCHA_REQUEST = 'GET_CAPTCHA_REQUEST';
export const GET_CAPTCHA_SUCCESS = 'GET_CAPTCHA_SUCCESS';
export const GET_CAPTCHA_ERROR = 'GET_CAPTCHA_ERROR';
export const RESET_CAPTCHA = 'RESET_CAPTCHA';
export function generateCaptchaRequestAction() {
    return { type: GET_CAPTCHA_REQUEST };
}
export function generateCaptchaSuccessAction(data) {
    return { type: GET_CAPTCHA_SUCCESS, data };
}
export function generateCaptchaErrorAction({ response, status }) {
    return { type: GET_CAPTCHA_ERROR, error: response, status };
}
export function resetCaptchaAction() {
    return { type: RESET_CAPTCHA };
}
export function generateCaptchaAction() {
    return (dispatch, getState) => {
        dispatch(generateCaptchaRequestAction());
        return ApiCaller.get(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.CAPTCHA })), { action: GET_CAPTCHA_REQUEST }))
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => dispatch(generateCaptchaSuccessAction(successData.response)), (errorData) => dispatch(generateCaptchaErrorAction(errorData)));
    };
}
export const EMI_CARDS_REQUEST = 'EMI_CARDS_REQUEST';
export const EMI_CARDS_SUCCESS = 'EMI_CARDS_SUCCESS';
export const EMI_CARDS_ERROR = 'EMI_CARDS_ERROR';
export function emiCardsRequestAction() {
    return { type: EMI_CARDS_REQUEST };
}
export function emiCardsSuccessAction(data) {
    return { type: EMI_CARDS_SUCCESS, data };
}
export function emiCardsErrorAction(error) {
    return { type: EMI_CARDS_ERROR, error };
}
export function fetchEmiCardsAction(tenureOptions) {
    return (dispatch, getState) => {
        dispatch(emiCardsRequestAction());
        const settings = Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.EMI_CARDS }), {
            action: EMI_CARDS_REQUEST,
            acceptErrors: false,
            acceptJson: true
        });
        Object.assign(tenureOptions, { tenure: Number(tenureOptions.tenure || 0) });
        return ApiCaller.post(settings, tenureOptions).then(data => {
            const savedCards = get(data, 'saved_cards', []);
            const selectedCard = savedCards.find(card => get(card, 'selected'));
            let cardId = get(selectedCard, 'card_information.card_id', '');
            // Set the default selected card as the new card option, but only do
            // this when there are no saved cards. There might be cases where there
            // are saved cards for a particular tenure, but none of them have the
            // 'selected' flag turned on. When this happens, we shouldn't
            // auto-expand the new card option, and should leave the choice to the
            // user.
            //
            // Otherwise, if there are saved cards rely on the selected option. If
            // there is no saved card with the 'selected' flag turned on, we just
            // show nothing as expanded.
            if (!savedCards.length) {
                cardId = constants.PAYMENT_INSTRUMENT.EMI_CREDIT_CARD;
            }
            // We know we're currently in EMI cards section since we run this
            // action only inside there. So the following updates the default
            // selected card based on the information we receive about the saved
            // cards for this combination of tenure and bank.
            dispatch(updateEmiCardPaymentOption(cardId));
            return dispatch(emiCardsSuccessAction(data));
        }, err => dispatch(emiCardsErrorAction(err)));
    };
}
export function selectWalletAction(token, instrumentType) {
    return (dispatch, getState) => {
        dispatch(getVoucherAddSelectAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.WALLET_SELECT })), { action: VOUCHER_ADD_OR_SELECT_REQUEST }), {
            token,
            payment_instrument: instrumentType
        })
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => {
            const updatedResponse = doNecessaryTransformations({ response: successData.response, getState });
            return dispatch(getVoucherAddSelectSuccessAction(updatedResponse));
        }, (errorData) => dispatch(getVoucherAddSelectErrorAction(errorData)));
    };
}
export function unselectWalletAction(token, transactionIds) {
    return (dispatch, getState) => {
        dispatch(getPaymentOptionsAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.WALLET_UNSELECT })), { action: VOUCHER_ADD_OR_SELECT_REQUEST }), {
            token,
            transaction_ids: transactionIds
        })
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => {
            const updatedResponse = doNecessaryTransformations({ response: successData.response, getState });
            return dispatch(getVoucherAddSelectSuccessAction(updatedResponse));
        }, (errorData) => dispatch(getVoucherAddSelectErrorAction(errorData)));
    };
}
export const PAY_VIA_WALLET_REQUEST = 'PAY_VIA_WALLET_REQUEST';
export const PAY_VIA_WALLET_SUCCESS = 'PAY_VIA_WALLET_SUCCESS';
export const PAY_VIA_WALLET_ERROR = 'PAY_VIA_WALLET_ERROR';
export function payViaWalletRequestAction() {
    return { type: PAY_VIA_WALLET_REQUEST };
}
export function payViaWalletSuccessAction(data) {
    return { type: PAY_VIA_WALLET_SUCCESS, data };
}
export function payViaWalletErrorAction({ response, status }) {
    return { type: PAY_VIA_WALLET_ERROR, error: response, status };
}
// Used in case full payment is happening through wallet(s) only
export function payViaWalletAction(token, transactionIds) {
    return (dispatch, getState) => {
        dispatch(payViaWalletRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.PROCESS_FULL_PAYMENT })), { action: PAY_VIA_WALLET_REQUEST }), {
            token,
            transaction_ids: transactionIds
        })
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => dispatch(payViaWalletSuccessAction(successData.response)), (errorData) => dispatch(payViaWalletErrorAction(errorData)));
    };
}
export const UPDATE_SELECTED_PAYMENT_OPTION = 'UPDATE_SELECTED_PAYMENT_OPTION';
export const UPDATE_EMI_CARD_PAYMENT_OPTION = 'UPDATE_EMI_CARD_PAYMENT_OPTION';
export function updateEmiCardPaymentOption(paymentOption) {
    return {
        type: UPDATE_EMI_CARD_PAYMENT_OPTION,
        data: paymentOption
    };
}
export function updatePaymentOption(paymentOption) {
    return {
        type: UPDATE_SELECTED_PAYMENT_OPTION,
        data: paymentOption
    };
}
// TODO: check for actual change, and only then trigger the action. This will
// avoid duplicate actions ovverriding some other state-specific items in some
// other reducer that might be listening to the above two actions.
//
// TODO: Move the dispatch arguments inside this function to separate action
// creators. Also, `data` is a generic key, change it to something else
// meaningful.
export function updateSelectedPaymentOptionAction(paymentOption) {
    return (dispatch, getState) => {
        const state = getState();
        const mainOption = state.payments.selectedPaymentOption.mainOption || null;
        const emiCard = state.payments.selectedPaymentOption.emiCard || null;
        const EMI_CARD_OPTIONS = (state.payments.paymentOptions.options || [])
            .filter(option => option.payment_instrument === constants.PAYMENT_INSTRUMENT.SAVED_CARD)
            .map(option => `EMI_${option.card_information.card_id}`);
        // Also consider cardIds that are unique for EMIs. Things like bajaj fin
        // serv cards etc.
        const emiCards = get(state, ['payments', 'emiCards', 'saved_cards'], []);
        emiCards.forEach(card => {
            const cardId = get(card, ['card_information', 'card_id']);
            if (cardId)
                EMI_CARD_OPTIONS.push(`EMI_${cardId}`);
        });
        // TODO: Move this into another constant, or import from emicardsection
        EMI_CARD_OPTIONS.push(`EMI_${constants.PAYMENT_INSTRUMENT.CREDIT_CARD}`);
        // if selected payment option is the same as the one that's already
        // selected, then return early. First check if the selected option is same
        // as the already selected emi card. If not, this might mean it's not an
        // EMI, so check with mainOption also. If it doesn't match both, then in
        // all likeliness it's a new option, so trigger the pending changes.
        if (emiCard === paymentOption || mainOption === paymentOption)
            return null;
        // When user goes to EMI payment, a `pay` call is made which sets a state
        // machine in the pz service. When user abandons EMI to move to saved
        // cards, for instance, a downtime message will get shown in instrument
        // check calls. Mobile app doesn't have this problem because they make a
        // fresh payment options call when moving from EMI page to the options
        // listing page. We are adding a hack to mimick the same functionality.
        // This will be solved once we move to passing transaction IDs for every
        // call, which needs some code changes in the API level.
        //
        // If selection is changed from EMI payment option to one other than emi
        // cards, make a payment options call, and return early.
        if ((mainOption === constants.PAYMENT_INSTRUMENT.EMI && EMI_CARD_OPTIONS.indexOf(paymentOption) === -1) ||
            mainOption === constants.PAYMENT_INSTRUMENT.NETBANKING) {
            // reset selection of payment options.
            dispatch(updatePaymentOption(''));
            dispatch(updateEmiCardPaymentOption(''));
            // Reset payment options, refetch the data. Pass the to-be-selected
            // payment option to this function so that it can be used after payment
            // options call succeeds.
            return dispatch(reFetchPaymentOptionsAction(paymentOption));
        }
        // If existing main option is an EMI option, and current selected option is
        // possibly an EMI card type, then trigger the EMI CARDS specific action,
        // and not the main state change action.
        // TODO: Figure out if this mainOption check is redundant, becuase we're
        // now using the EMI-specific cardKey instead of the card id
        if (EMI_CARD_OPTIONS.indexOf(paymentOption) > -1) {
            return dispatch(updateEmiCardPaymentOption(paymentOption));
        }
        // If current option is not an EMI card, then clear the state of the
        // emiCard key in selected payment option state
        dispatch(updateEmiCardPaymentOption(''));
        return dispatch(updatePaymentOption(paymentOption));
    };
}
export const OTP_MODES_REQUEST = 'OTP_MODES_REQUEST';
export const OTP_MODES_SUCCESS = 'OTP_MODES_SUCCESS';
export const OTP_MODES_ERROR = 'OTP_MODES_ERROR';
export const RESET_OTP_MODES = 'RESET_OTP_MODES';
export function otpModesRequestAction() {
    return { type: OTP_MODES_REQUEST };
}
export function otpModesSuccessAction(data) {
    return { type: OTP_MODES_SUCCESS, data };
}
export function otpModesErrorAction({ response, status }) {
    return { type: OTP_MODES_ERROR, error: response, status };
}
export function resetOTPModesAction() {
    return { type: RESET_OTP_MODES };
}
export function resendOtpModesAction(transactionId) {
    return (dispatch, getState) => {
        dispatch(otpModesRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.RESEND_OTP })), { action: OTP_MODES_REQUEST }), { txn_id: transactionId })
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => dispatch(otpModesSuccessAction(successData.response)), (errorData) => dispatch(otpModesErrorAction(errorData)));
    };
}
export function fetchOtpModesAction(transactionId) {
    return (dispatch, getState) => {
        dispatch(otpModesRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.OTP })), { action: OTP_MODES_REQUEST }), { txn_id: transactionId })
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => dispatch(otpModesSuccessAction(successData.response)), (errorData) => dispatch(otpModesErrorAction(errorData)));
    };
}
export function otpFallbackAction(transactionId) {
    return (dispatch, getState) => {
        dispatch(payWithDetailsRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.OTP_FALLBACK_MODE })), { action: OTP_MODES_REQUEST, acceptErrors: false, acceptJson: true }), { txn_id: transactionId }).then(data => dispatch(payWithDetailsSuccessAction(data)), err => dispatch(payWithDetailsErrorAction({ response: err, status: constants.NETWORK_ERROR.NO_STATUS_CODE })));
    };
}
export const OTP_SUBMIT_REQUEST = 'OTP_SUBMIT_REQUEST';
export const OTP_SUBMIT_SUCCESS = 'OTP_SUBMIT_SUCCESS';
export const OTP_SUBMIT_ERROR = 'OTP_SUBMIT_ERROR';
export function otpSubmitRequestAction() {
    return { type: OTP_SUBMIT_REQUEST };
}
export function otpSubmitSuccessAction(data) {
    return { type: OTP_SUBMIT_SUCCESS, data };
}
export function otpSubmitErrorAction({ response, status }) {
    return { type: OTP_SUBMIT_ERROR, error: response, status };
}
export function otpSubmitAction(params) {
    return (dispatch, getState) => {
        dispatch(otpSubmitRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.OTP_AUTH })), { action: OTP_SUBMIT_REQUEST }), params)
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => dispatch(otpSubmitSuccessAction(successData.response)), (errorData) => dispatch(otpSubmitErrorAction(errorData)));
    };
}
export const RESET_ERROR_OVERLAY_ACTION = 'RESET_ERROR_OVERLAY_ACTION';
export function resetErrorOverlayAction() {
    return { type: RESET_ERROR_OVERLAY_ACTION };
}
export var ADVANCE_PAYMENT_BREAKUP_ACTION_STATE;
(function (ADVANCE_PAYMENT_BREAKUP_ACTION_STATE) {
    ADVANCE_PAYMENT_BREAKUP_ACTION_STATE["REQUEST"] = "ADVANCE_PAYMENT_BREAKUP_REQUEST";
    ADVANCE_PAYMENT_BREAKUP_ACTION_STATE["SUCCESS"] = "ADVANCE_PAYMENT_DETAILS_SUCCESS";
    ADVANCE_PAYMENT_BREAKUP_ACTION_STATE["ERROR"] = "ADVANCE_PAYMENT_DETAILS_ERROR";
})(ADVANCE_PAYMENT_BREAKUP_ACTION_STATE || (ADVANCE_PAYMENT_BREAKUP_ACTION_STATE = {}));
export function advancePaymentBreakupRequestAction() {
    return { type: ADVANCE_PAYMENT_BREAKUP_ACTION_STATE.REQUEST };
}
export function advancePaymentBreakupSuccessAction(data) {
    return { type: ADVANCE_PAYMENT_BREAKUP_ACTION_STATE.SUCCESS, data };
}
export function advancePaymentBreakupErrorAction({ response, status }) {
    return { type: ADVANCE_PAYMENT_BREAKUP_ACTION_STATE.ERROR, error: response, status };
}
export function advancePaymentBreakupAction(params) {
    return (dispatch, getState) => {
        dispatch(advancePaymentBreakupRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.ADVANCE_PAYMENT_BREAKUP })), { action: ADVANCE_PAYMENT_BREAKUP_ACTION_STATE.REQUEST }), params)
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => dispatch(advancePaymentBreakupSuccessAction(successData.response)), (errorData) => dispatch(advancePaymentBreakupErrorAction(errorData)));
    };
}
export var SELECT_INSTRUMENT_REQUEST_STATE;
(function (SELECT_INSTRUMENT_REQUEST_STATE) {
    SELECT_INSTRUMENT_REQUEST_STATE["REQUEST"] = "SELECT_INSTRUMENT_REQUEST";
    SELECT_INSTRUMENT_REQUEST_STATE["SUCCESS"] = "SELECT_INSTRUMENT_SUCCESS";
    SELECT_INSTRUMENT_REQUEST_STATE["ERROR"] = "SELECT_INSTRUMENT_ERROR";
})(SELECT_INSTRUMENT_REQUEST_STATE || (SELECT_INSTRUMENT_REQUEST_STATE = {}));
export function selectInstrumentRequestAction() {
    return { type: SELECT_INSTRUMENT_REQUEST_STATE.REQUEST };
}
export function selectInstrumentSuccessAction(data, requestParams) {
    // Mutating backend response to trigger navigation to /payments page as an external redirection (only when instrument being selected is COD)
    const { payment_instrument } = requestParams;
    if (payment_instrument === constants.PAYMENT_INSTRUMENT.CASH_DELIVERY) {
        const responseType = get(data, 'response_type');
        const responseStatus = get(data, 'response_status');
        const action_type = get(data, 'primary_action.action_type');
        if (responseStatus === RESPONSE_STATUS.SUCCESS &&
            responseType === RESPONSE_TYPE.ACCEPT_PAYMENT_DETAILS &&
            action_type) {
            data.primary_action.action_type = PAYMENT_REDIRECTION.EXTERNAL;
            // TODO: come up with a way to ensure the path here and in routes.js remain in sync. - constants file?
            data.primary_action.target = '/payments';
            data.primary_action.parameters = data.primary_action.parameters || {};
            if (!data.primary_action.parameters.token) {
                data.primary_action.parameters = Object.assign(data.primary_action.parameters, { token: requestParams.token });
            }
        }
    }
    return { type: SELECT_INSTRUMENT_REQUEST_STATE.SUCCESS, data };
}
export function selectInstrumentErrorAction({ response, status }) {
    return { type: SELECT_INSTRUMENT_REQUEST_STATE.ERROR, error: response, status };
}
export function selectInstrumentAction(params) {
    return (dispatch, getState) => {
        dispatch(selectInstrumentRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, {
            pathname: constants.API_ENDPOINT_NAME.SELECT_INSTRUMENT,
            payment_instrument: params.payment_instrument
        })), { action: SELECT_INSTRUMENT_REQUEST_STATE.REQUEST }), params)
            .then(normalizeSuccess, normalizeError)
            .then(checkForResponseStatus)
            .then((successData) => dispatch(selectInstrumentSuccessAction(successData.response, params)), (errorData) => dispatch(selectInstrumentErrorAction(errorData)));
    };
}
export const MOBILE_WALLET_OPTIONS_REQUEST = 'MOBILE_WALLET_OPTIONS_REQUEST';
export const MOBILE_WALLET_OPTIONS_SUCCESS = 'MOBILE_WALLET_OPTIONS_SUCCESS';
export const MOBILE_WALLET_OPTIONS_ERROR = 'MOBILE_WALLET_OPTIONS_ERROR';
function mobileWalletOptionsRequestAction() {
    return { type: MOBILE_WALLET_OPTIONS_REQUEST };
}
function mobileWalletOptionsSuccessAction(data) {
    return { type: MOBILE_WALLET_OPTIONS_SUCCESS, data };
}
function mobileWalletOptionsErrorAction(error) {
    return { type: MOBILE_WALLET_OPTIONS_ERROR, error };
}
export function fetchMobileWalletOptionsAction(token, payment_instrument) {
    return (dispatch, getState) => {
        dispatch(mobileWalletOptionsRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.WALLET_OPTIONS })), { action: MOBILE_WALLET_OPTIONS_REQUEST, acceptErrors: false, acceptJson: true }), { token, payment_instrument }).then(data => dispatch(mobileWalletOptionsSuccessAction(data)), error => dispatch(mobileWalletOptionsErrorAction(error)));
    };
}
export const MOBILE_WALLET_OTP_REQUEST = 'MOBILE_WALLET_OTP_REQUEST';
export const MOBILE_WALLET_OTP_LOADING = 'MOBILE_WALLET_OTP_LOADING';
export const MOBILE_WALLET_OTP_SUCCESS = 'MOBILE_WALLET_OTP_SUCCESS';
export const MOBILE_WALLET_OTP_ERROR = 'MOBILE_WALLET_OTP_ERROR';
export function loadWalletOtpAction() {
    return { type: MOBILE_WALLET_OTP_LOADING };
}
function mobileWalletOtpRequestAction() {
    return { type: MOBILE_WALLET_OTP_REQUEST };
}
function mobileWalletOtpSuccessAction(data) {
    return { type: MOBILE_WALLET_OTP_SUCCESS, data };
}
function mobileWalletOtpErrorAction(error) {
    return { type: MOBILE_WALLET_OTP_ERROR, error };
}
export function generateOtpForWalletAction(params) {
    return (dispatch, getState) => {
        dispatch(mobileWalletOtpRequestAction());
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.GENERATE_WALLET_OTP })), { action: MOBILE_WALLET_OTP_REQUEST, acceptErrors: false, acceptJson: true }), Object.assign({}, params)).then(data => dispatch(mobileWalletOtpSuccessAction(data)), error => dispatch(mobileWalletOtpErrorAction(error)));
    };
}
const MOBILE_WALLET_OTP_VALIDATE_REQUEST = 'MOBILE_WALLET_OTP_VALIDATE_REQUEST';
const MOBILE_WALLET_OTP_VALIDATE_SUCCESS = 'MOBILE_WALLET_OTP_VALIDATE_SUCCESS';
export const MOBILE_WALLET_OTP_VALIDATE_ERROR = 'MOBILE_WALLET_OTP_VALIDATE_ERROR';
function mobileWalletOtpValidateSuccessAction(data) {
    return { type: MOBILE_WALLET_OTP_VALIDATE_SUCCESS, data };
}
function mobileWalletOtpValidateErrorAction(error) {
    return { type: MOBILE_WALLET_OTP_VALIDATE_ERROR, error };
}
export function validateOtpForWalletAction(params) {
    return (dispatch, getState) => {
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.VALIDATE_WALLET_OTP })), { action: MOBILE_WALLET_OTP_VALIDATE_REQUEST, acceptErrors: false, acceptJson: true }), Object.assign({}, params)).then(data => dispatch(mobileWalletOtpValidateSuccessAction(data)), error => dispatch(mobileWalletOtpValidateErrorAction(error)));
    };
}
export const MOBILE_WALLET_BALANCE_REQUEST = 'MOBILE_WALLET_BALANCE_REQUEST';
export const MOBILE_WALLET_BALANCE_SUCCESS = 'MOBILE_WALLET_BALANCE_SUCCESS';
export const MOBILE_WALLET_BALANCE_ERROR = 'MOBILE_WALLET_BALANCE_ERROR';
function mobileWalletBalanceRequestAction(walletApps) {
    return { type: MOBILE_WALLET_BALANCE_REQUEST, walletApps };
}
function mobileWalletBalanceSuccessAction(data, walletApps) {
    return { type: MOBILE_WALLET_BALANCE_SUCCESS, data, walletApps };
}
function mobileWalletBalanceErrorAction(error, walletApps) {
    return { type: MOBILE_WALLET_BALANCE_ERROR, error, walletApps };
}
export function fetchMobileWalletBalanceAction(request, retryCount = 0) {
    const { wallet_apps, token, payment_instrument } = request;
    return fetchMobileWalletBalanceWithRetry(wallet_apps, token, payment_instrument, retryCount);
}
function fetchMobileWalletBalanceWithRetry(wallet_apps, token, payment_instrument, retryCount = 0) {
    return (dispatch, getState) => {
        dispatch(mobileWalletBalanceRequestAction(wallet_apps));
        return ApiCaller.post(Object.assign(Object.assign({}, getPaymentAjaxConfig(getState, { pathname: constants.API_ENDPOINT_NAME.WALLET_BALANCE })), { action: MOBILE_WALLET_BALANCE_REQUEST, acceptErrors: false, acceptJson: true }), { wallet_apps, token, payment_instrument }).then(response => {
            const failedWalletApps = get(response, 'wallet_details', [])
                .filter((wallet) => wallet.retry)
                .map((wallet) => wallet.app_code);
            // maintaining an extra retry count check on UI side,
            // in case server keeps sending retry as true for some wallets
            if (failedWalletApps.length && retryCount < 3) {
                dispatch(fetchMobileWalletBalanceWithRetry(failedWalletApps, token, payment_instrument, retryCount + 1));
            }
            return dispatch(mobileWalletBalanceSuccessAction(response, wallet_apps));
        }, error => dispatch(mobileWalletBalanceErrorAction(error, wallet_apps)));
    };
}
